import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { CollapsibleList } from '@/features/proactive-agents/CollapsibleList';
import { EmailPickerFields } from '@/features/proactive-agents/EmailPickerFields';
import { NudgeDeliveryStatus } from '@/features/proactive-agents/NudgeDeliveryStatus';
import { plural } from '@/features/proactive-agents/plural';
import type {
  NudgeRecipient,
  PreviewTarget,
  SendNudgeRequest,
} from '@/features/proactive-agents/proactive-agents-types';
import { useEmailPicker } from '@/features/proactive-agents/useEmailPicker';
import { useLastNudgeSend } from '@/features/proactive-agents/useLastNudgeSend';
import {
  mayHaveStarted,
  useAgentChatCounts,
  useNudgeRecipients,
  useSendNudge,
} from '@/features/proactive-agents/useProactiveAgents';

// Same limit as the backend
const MAX_MESSAGE_LENGTH = 100_000;
const MESSAGE_COUNT_SHOWN_FROM = MAX_MESSAGE_LENGTH * 0.9;
// Above this, the dialog says how long the send will take
const LARGE_SEND_CHATS = 1_000;

// Sends the server has not accepted, kept across reloads per admin so Retry can resend each with its own id
const unfinishedSendsKey = (adminEmail: string) =>
  `proactive-agents.unfinished-nudges:${adminEmail}`;

const isSendRequest = (value: unknown): value is SendNudgeRequest => {
  const request = value as Partial<SendNudgeRequest> | null;
  return (
    typeof request?.nudge_id === 'string' &&
    typeof request.agent === 'string' &&
    typeof request.prompt === 'string' &&
    Array.isArray(request.emails) &&
    Array.isArray(request.chat_ids)
  );
};

const readUnfinishedSends = (adminEmail: string): SendNudgeRequest[] => {
  try {
    const stored: unknown = JSON.parse(
      window.localStorage.getItem(unfinishedSendsKey(adminEmail)) ?? '[]'
    );
    return Array.isArray(stored) ? stored.filter(isSendRequest) : [];
  } catch {
    return [];
  }
};

// Returns whether the sends were saved, so the page can say when Retry will not survive a reload
const saveUnfinishedSends = (
  adminEmail: string,
  requests: SendNudgeRequest[]
): boolean => {
  try {
    window.localStorage.setItem(
      unfinishedSendsKey(adminEmail),
      JSON.stringify(requests)
    );
    return true;
  } catch {
    return false;
  }
};

const describeRecipients = (target: PreviewTarget) =>
  target.emails.length
    ? `the ${plural(target.emails.length, 'listed email')}`
    : target.email_contains
      ? `emails containing "${target.email_contains}"`
      : 'everyone with this agent';

// One item per chat, keyed by chat id, since one owner can have several chats of an agent
const chatItems = (recipients: NudgeRecipient[]) =>
  recipients.map((recipient) => ({
    key: recipient.chat_id,
    text: recipient.email,
  }));

// Keyed on `adminEmail` by the page, so another admin's sends are read afresh
export const SendNudgeSection = ({ adminEmail }: { adminEmail: string }) => {
  const agentsQuery = useAgentChatCounts();
  const agents = agentsQuery.data ?? [];
  const [chosenAgent, setChosenAgent] = useState('');
  const [prompt, setPrompt] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [unfinishedSends, setUnfinishedSends] = useState(() =>
    readUnfinishedSends(adminEmail)
  );
  const [unfinishedSendsSaved, setUnfinishedSendsSaved] = useState(true);
  const [lastSend, rememberLastSend] = useLastNudgeSend(adminEmail);
  const agent = chosenAgent || agents[0]?.agent || '';
  const picker = useEmailPicker(agent, {
    everyoneLabel: 'Everyone with this agent',
  });
  const recipientsQuery = useNudgeRecipients(picker.target);
  const sendNudge = useSendNudge();
  const recipients = recipientsQuery.data?.recipients ?? [];
  // Never send to a list the admin has not seen for the current input
  const previewIsCurrent =
    picker.inputIsSettled &&
    recipientsQuery.isSuccess &&
    !recipientsQuery.isFetching;
  const recipientsText = picker.target ? describeRecipients(picker.target) : '';
  const canSend =
    picker.target !== null &&
    previewIsCurrent &&
    recipients.length > 0 &&
    prompt.trim().length > 0 &&
    !sendNudge.isPending;

  const updateUnfinishedSends = (
    update: (sends: SendNudgeRequest[]) => SendNudgeRequest[]
  ) =>
    setUnfinishedSends((sends) => {
      const updated = update(sends);
      setUnfinishedSendsSaved(saveUnfinishedSends(adminEmail, updated));
      return updated;
    });
  const forgetSend = (nudgeId: string) =>
    updateUnfinishedSends((sends) =>
      sends.filter((send) => send.nudge_id !== nudgeId)
    );

  // Kept until the server accepts it, so a failed send can be retried with the same id
  const send = (request: SendNudgeRequest, onAccepted?: () => void) => {
    updateUnfinishedSends((sends) => [
      ...sends.filter((send) => send.nudge_id !== request.nudge_id),
      request,
    ]);
    sendNudge.mutate(request, {
      onSuccess: (result) => {
        forgetSend(request.nudge_id);
        rememberLastSend({ nudge_id: result.nudge_id, agent: request.agent });
        onAccepted?.();
      },
    });
  };

  // Every Send is a new message; Retry is what resends one to the chats it missed
  const handleSend = () => {
    if (!picker.target || !canSend) return;
    setConfirming(false);
    send(
      {
        ...picker.target,
        prompt: prompt.trim(),
        nudge_id: crypto.randomUUID(),
        chat_ids: recipients.map((recipient) => recipient.chat_id),
      },
      () => setPrompt('')
    );
  };
  const retrySends = unfinishedSends.filter(
    (request) =>
      !sendNudge.isPending || sendNudge.variables?.nudge_id !== request.nudge_id
  );

  return (
    <Card role="region" aria-labelledby="nudge_section_title">
      <CardHeader>
        <CardTitle id="nudge_section_title">Send a message to agents</CardTitle>
        <CardDescription>
          Send a message from the Littlebird team to users&apos; proactive
          agents. Each chat of the chosen agent gets one turn with the message,
          if it is active, has had its first turn, and belongs to an active user
          who is internal or has the proactive-agent flag. Counts and previews
          also include owners who are no longer internal or flagged; their turn
          refuses the message, so the delivery status lists them as failed.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid max-w-3xl gap-6">
        {agentsQuery.error ? (
          <p role="alert" className="text-sm text-destructive">
            {agentsQuery.error.message}
          </p>
        ) : null}
        <div className="grid gap-2 text-sm">
          <label htmlFor="nudge_agent">Agent</label>
          <Select
            id="nudge_agent"
            value={agent}
            onChange={(event) => setChosenAgent(event.target.value)}
            disabled={!agents.length}
          >
            {agents.map((agentCount) => (
              <option key={agentCount.agent} value={agentCount.agent}>
                {agentCount.agent} ({plural(agentCount.chat_count, 'chat')})
              </option>
            ))}
          </Select>
          {agentsQuery.isLoading ? (
            <p className="text-muted-foreground">Loading...</p>
          ) : agentsQuery.isSuccess && !agents.length ? (
            <p className="text-muted-foreground">
              No user has an active proactive agent.
            </p>
          ) : null}
        </div>

        <EmailPickerFields label="Recipients" picker={picker} />

        <div className="grid gap-2 text-sm">
          <label className="grid gap-2">
            Message
            <Textarea
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              rows={6}
              maxLength={MAX_MESSAGE_LENGTH}
              placeholder="What should the agents do?"
            />
          </label>
          {prompt.length > MESSAGE_COUNT_SHOWN_FROM ? (
            <p className="text-xs text-muted-foreground">
              {prompt.length.toLocaleString()} /{' '}
              {MAX_MESSAGE_LENGTH.toLocaleString()} characters
            </p>
          ) : null}
        </div>

        {picker.target && recipientsQuery.data ? (
          <div className="grid gap-3">
            <p className="text-sm">
              {plural(recipients.length, 'chat')} will get this message.
            </p>
            {/* Everyone mode shows only the count, not every owner's email */}
            {picker.mode === 'everyone' ? null : (
              <CollapsibleList
                title="Recipients"
                items={chatItems(recipients)}
                open
              />
            )}
            <CollapsibleList
              title="No chat of this agent that would get it"
              items={recipientsQuery.data.unmatched_emails.map((email) => ({
                key: email,
                text: email,
              }))}
              open
            />
          </div>
        ) : null}
        {recipientsQuery.error ? (
          <p role="alert" className="text-sm text-destructive">
            {recipientsQuery.error.message}
          </p>
        ) : null}

        <div>
          <Button
            type="button"
            disabled={!canSend}
            onClick={() => setConfirming(true)}
          >
            {sendNudge.isPending ? 'Sending...' : 'Send'}
          </Button>
        </div>
        {sendNudge.error ? (
          <div role="alert" className="grid gap-1 text-sm text-destructive">
            <p>{sendNudge.error.message}</p>
            {mayHaveStarted(sendNudge.error) ? (
              <p>
                Some chats may already have the message; use Retry below so they
                get no second turn.
              </p>
            ) : null}
          </div>
        ) : null}
        {lastSend ? (
          <NudgeDeliveryStatus
            key={lastSend.nudge_id}
            nudgeId={lastSend.nudge_id}
            agent={lastSend.agent}
          />
        ) : null}
        {retrySends.length ? (
          <div className="grid gap-3">
            <p className="text-sm font-medium">
              These messages may not have been sent
            </p>
            {unfinishedSendsSaved ? null : (
              <p className="text-sm text-muted-foreground">
                This browser could not save them, so Retry works only until the
                page reloads.
              </p>
            )}
            {retrySends.map((request) => (
              <div
                key={request.nudge_id}
                className="grid gap-2 rounded-md border px-4 py-3 text-sm"
              >
                <p>
                  {request.agent}, {describeRecipients(request)}
                </p>
                <p className="line-clamp-3 whitespace-pre-wrap break-words text-muted-foreground">
                  {request.prompt}
                </p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    disabled={sendNudge.isPending}
                    onClick={() => send(request)}
                  >
                    Retry
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={sendNudge.isPending}
                    onClick={() => forgetSend(request.nudge_id)}
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </CardContent>

      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Send to {plural(recipients.length, 'chat')} of {agent}?
            </DialogTitle>
            <DialogDescription>
              Recipients: {recipientsText}. Each agent runs a full turn with
              this message, one every 10 seconds.
            </DialogDescription>
          </DialogHeader>
          {recipients.length > LARGE_SEND_CHATS ? (
            <p role="alert" className="text-sm text-destructive">
              Sends to more than {LARGE_SEND_CHATS.toLocaleString()} chats are
              large. Every chat&apos;s turn stores its own copy of the message,
              and the last turn starts about{' '}
              {Math.round((recipients.length * 10) / 3600)} hours from now.
            </p>
          ) : null}
          <p className="max-h-64 overflow-y-auto whitespace-pre-wrap break-words rounded-md bg-muted px-4 py-3 text-sm">
            {prompt.trim()}
          </p>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="button" disabled={!canSend} onClick={handleSend}>
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
